import {
  ListBookedConsultationsRequest,
  BookConsultationRequest,
  UnbookConsultationRequest,
  DownloadConsultationCalendarRequest,
} from '@taxfyle/api-internal/internal/consultation_pb'
import { ClaimConsultationJobRequest } from '@taxfyle/api-internal/internal/job_pb'
import type { TimeRange } from '@/infra/time-range'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import type { FetchJobFn, PickupResult } from '@/features/jobs'
import type { JobsApi } from '@/features/jobs/api/job-api'
import type { PrimeRoomFn } from '@/features/messaging'
import { Consultation } from './Consultation'
import { mapBookedConsultationProtoToProps } from './consultation-mappers'

/**
 * Loads, books and claims consultations.
 */
export class ConsultationStore {
  /**
   * Consultations booked by the current user.
   */
  readonly bookedConsultations = collection({
    create: () => new Consultation(),
    getModelId: (m) => m.id,
    getDataId: (d) => d.id,
  })

  constructor(
    private readonly _api: JobsApi,
    private readonly _fetchJob: FetchJobFn,
    private readonly _primeRoom: PromiseLike<PrimeRoomFn>,
  ) {
    this.fetchBookedConsultations = this.fetchBookedConsultations.bind(this)
    this.pickupConsultationJob = this.pickupConsultationJob.bind(this)
  }

  /**
   * Fetches the consultations booked by the current user.
   */
  async fetchBookedConsultations(
    signal?: AbortSignal,
  ): Promise<Consultation[]> {
    const response = await this._api.consultations.listBookedConsultations(
      new ListBookedConsultationsRequest(),
    )
    const primeRoom = await this._primeRoom
    signal?.throwIfAborted()
    return this.bookedConsultations.set(
      response
        .getAppointmentsList()
        .map((c) => mapBookedConsultationProtoToProps(c, primeRoom)),
    )
  }

  /**
   * Claims a consultation job.
   *
   * A booked time is not required for tax jobs but is required for
   * consultation jobs.
   *
   * @param id
   * @param bookedTime
   */
  async pickupConsultationJob(
    id: Id,
    bookedTime?: TimeRange,
  ): Promise<PickupResult> {
    const jobId = Id.assert(id)
    const request = new ClaimConsultationJobRequest().setId(jobId)

    if (bookedTime) {
      request.setBookedTime(bookedTime.toProtoInterval())
    }

    await this._api.jobs.claimConsultationJob(request)

    await this._fetchJob(jobId, { force: true })

    return 'Success'
  }

  /**
   * Books a consultation.
   *
   * @param jobId
   * @param bookedTime
   */
  async bookConsultation(jobId: Id, bookedTime: TimeRange) {
    jobId = Id.assert(jobId)
    await this._api.consultations.bookConsultation(
      new BookConsultationRequest()
        .setId(jobId)
        .setBookedTime(bookedTime.toProtoInterval()),
    )

    await this._fetchJob(jobId, { force: true })
  }

  /**
   * Withdraws from a consultation.
   *
   * @param jobId
   */
  async unbookConsultation(jobId: Id) {
    jobId = Id.assert(jobId)
    await this._api.consultations.unbookConsultation(
      new UnbookConsultationRequest().setId(jobId),
    )
    await this._fetchJob(jobId, { force: true })
  }

  /**
   * Fetches the calendar file of a consultation.
   *
   * @param jobId
   */
  async fetchConsultationCalendar(jobId: Id): Promise<File> {
    const response = await this._api.consultations.downloadConsultationCalendar(
      new DownloadConsultationCalendarRequest().setId(Id.assert(jobId)),
    )
    return new File([response.getData_asU8() as BlobPart], 'calendar.ics', {
      type: 'text/calendar',
    })
  }
}
